import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActionArea from '../../components/ActionArea'
import Head from '../../components/Head'
import List from '../../components/List'
import Button from '../../components/UI/Button'
import SmartSearch from '../../components/UI/SmartSearch'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { deleteBingoGameById, getBingoDefinitionsList } from '../../services/bingoDefinitions'
import direction from '../../utils/filterDirectionOrder'
import BingoModal from './BingoModal'
import { stringCompare, transformToNumberAndCompare } from '../../utils/sorters'
import {numberToCurrency} from "../../utils/objectUtils";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { Modal } from 'antd'
import { notificationError, notificationSucces } from '../../services/notification'

type BingoDefinitions = {
  gameId: string,
  lobbyOrdering: string,
  manufacturer: string,
  title: string,
  wager: string,
  payout: string,
  active: string,
  bingoDefinitionId: string,
  bingoPrizeId: string,
  slotDefinitionId: string,
  checksum: string,
  packageChecksum: string,
}

type BingoDefinitionsList = BingoDefinitions[]

function index(props: {accessLevel: string}) {
  const log = {
    log_type: 'visited',
    log_object: 'bingo_gamelist'
  }
  const loading = useLoading()
  const {canEdit} = useAuth()
  const history = useNavigate()
  const [search, setSearch] = useState({
    text: '',
    like: '',
    fieldOrder: '',
    directionOrder: '',
  })
  const [validSearch, setValidSearch] = useState(true)
  const [bingoDefinitionsList, setBingoDefinitionsList] = useState<BingoDefinitionsList>([])
  const [modal, setModal] = useState({
    visible: false,
    game_id: '',
    bingo_definition_id: '',
    bingo_prize_id: '',
    slot_definition_id: ''
  })
  const [isModalVisible, setIsModalVisible] = useState({
    visible: false,
    idToDelete: '' 
  })

  const showModal = (value: any) => {
    setModal({
      visible: true,
      game_id: value.gameId,
      bingo_definition_id: value.bingoDefinitionId,
      bingo_prize_id: value.bingoPrizeId,
      slot_definition_id: value.slotDefinitionId
    })
  }

  useEffect(() => {
    sendSystemLog(log);
  }, []);


  const getActionButtons = (game: any) => {
    return (
      <div className="roles__button-actions" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap'}}>
        <Button type="primary" onClick={() => showModal(game)}
        style={{ backgroundColor: '#2fa4e7' }}
        >
        <EyeOutlined style={{ fontSize: '16px', color: '#FFF' }} />   
        </Button>
        <Button
          onClick={() => {
            setIsModalVisible({visible: true, idToDelete: game.gameId})
          }}
          type='primary'
          style={{ backgroundColor: '#d41d24' }}
        >
          <DeleteOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
      </div>
    )
  }

  const deleteGame = async () => {
    if (!isModalVisible.idToDelete) return notificationError('Could not find game')
    loading.addLoading()
    const deleted = await deleteBingoGameById({id: isModalVisible.idToDelete})
    if (deleted) {
        const res = bingoDefinitionsList.filter(obj => obj.gameId !== isModalVisible.idToDelete);
        setBingoDefinitionsList(res)
    }
    
    loading.removeLoading()
    setIsModalVisible({visible: false, idToDelete: ''})
}


  const onRefresh = async (order?: string, directionparam?: string) => {
    loading.addLoading()
    let requestParams = {
      like: search.like,
      fieldOrder: '',
      directionOrder: ''
    }
    if(order && directionparam) {
      requestParams = {
        ...requestParams,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      }
      setSearch({
        ...search,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      })
    }
    const res = await getBingoDefinitionsList(requestParams) 
    setBingoDefinitionsList(res)
    loading.removeLoading()
  }

  const closeModal = () => {
    setModal({
      visible: false,
      game_id: '',
      bingo_definition_id: '',
      bingo_prize_id: '',
      slot_definition_id: ''
    })
  }
  
  return (
    <div className="bingo-definitions">
      <meta name="viewport" content="width=device-width, initial-scale=1"/>
      <BingoModal {...modal} close={closeModal}/>
      <Modal 
        title={`Are you sure you want to remove game ${isModalVisible.idToDelete}?`}
        open={isModalVisible.visible}
        onOk={() => deleteGame()} 
        onCancel={() => setIsModalVisible({...isModalVisible, visible: false})} 
      />
      <Head 
        title="Roles" 
        buttonAction={() => history(`../bingogamelist/upload/`)} 
        buttonText="Add or Update Game" 
        disabled={!canEdit}
      />
      <ActionArea buttonAction={onRefresh} buttonText={'Search'} disabled={!validSearch}>
        <SmartSearch
          value={search.text}
          onChange={(e: string) => setSearch({...search, text: e})}
          label='Smart Search'
          status="active"
          onValidSearch={setValidSearch}
          searchBy={['bingodefinition']}
          onObjValueChange={(e: any) => setSearch({...search, like: e.title})}
          type="bingodefinition"
          placeholder="title or form"
          validOnEmpty
        />
      </ActionArea>
      <List
        rows={bingoDefinitionsList}
        columns={[
          {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (value: string) => value,
            width: 200,
            sorter: (a: BingoDefinitions, b: BingoDefinitions) => stringCompare(a.title, b.title),
          },
          {
            title: 'Form',
            dataIndex: 'gameId',
            key: 'gameId',
            render: (value: string) => value,
            width: 150,
            sorter: (a: BingoDefinitions, b: BingoDefinitions) => transformToNumberAndCompare(a.gameId, b.gameId),
          },
          {
            title: 'Wager',
            dataIndex: 'wager',
            key: 'wager',
            render: (value: string) => numberToCurrency(value),
            width: 150,
            sorter: (a: BingoDefinitions, b: BingoDefinitions) => transformToNumberAndCompare(a.wager, b.wager),
          },
          {
            title: 'Payout',
            dataIndex: 'payout',
            key: 'payout',
            render: (value: string) => value,
            width: 150,
            sorter: (a: BingoDefinitions, b: BingoDefinitions) => transformToNumberAndCompare(a.payout, b.payout),
          },
          {
            title: 'Checksum',
            dataIndex: 'packageChecksum',
            key: 'packageChecksum',
            render: (value: string) => value,
            width: 300,
            sorter: (a: BingoDefinitions, b: BingoDefinitions) => stringCompare(a.packageChecksum, b.packageChecksum),
          },
          {
            title: 'Actions',
            dataIndex: '',
            key: 'actions',
            render: (value: string) =>getActionButtons(value),
            width: 150
          },
        ]}
      />
    </div>
  )
}

export default index


